import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { ReservationBooking } from '../../services/reservation-system/reservation-board.service';
import {
  formatRsBookingTimeRange,
  rsBookingDisplayName,
} from '../../utils/reservationBoardCalendar.util';
import ReservationFormLabel from './ReservationFormLabel';

type RsBoardCalendarModalsProps = {
  showResourceEdit: boolean;
  editResourceName: string;
  onEditResourceNameChange: (value: string) => void;
  onCloseResourceEdit: () => void;
  onSubmitResourceEdit: () => void;
  resourceEditPending: boolean;
  deleteTarget: ReservationBooking | null;
  deletePasswordInput: string;
  onDeletePasswordInputChange: (value: string) => void;
  onCloseDeleteModal: () => void;
  onSubmitDelete: () => void;
  deletePending: boolean;
  /** モーダル表示中の削除エラー（ページ flash は backdrop の裏に隠れる） */
  deleteErrorMessage: string | null;
  showOverlapModal: boolean;
  onCloseOverlapModal: () => void;
};

export default function RsBoardCalendarModals({
  showResourceEdit,
  editResourceName,
  onEditResourceNameChange,
  onCloseResourceEdit,
  onSubmitResourceEdit,
  resourceEditPending,
  deleteTarget,
  deletePasswordInput,
  onDeletePasswordInputChange,
  onCloseDeleteModal,
  onSubmitDelete,
  deletePending,
  deleteErrorMessage,
  showOverlapModal,
  onCloseOverlapModal,
}: RsBoardCalendarModalsProps) {
  const { t } = useTranslation();
  const deleteSummary =
    deleteTarget != null
      ? [
          deleteTarget.resourceName,
          formatRsBookingTimeRange(
            deleteTarget.startTime,
            deleteTarget.endTime,
            t('reservation.board.timeRangeSeparator', { defaultValue: '〜' }),
            t('reservation.board.overnightPrefix', { defaultValue: '翌' })
          ),
          rsBookingDisplayName(deleteTarget),
        ]
          .filter(Boolean)
          .join(' · ')
      : '';

  return (
    <>
      <Modal show={showResourceEdit} onHide={onCloseResourceEdit} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('reservation.board.editResourceTitle', { defaultValue: '項目名の編集' })}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              onSubmitResourceEdit();
            }}
          >
            <Form.Group>
              <Form.Label>{t('reservation.board.resource', { defaultValue: '項目' })}</Form.Label>
              <Form.Control
                value={editResourceName}
                onChange={(e) => onEditResourceNameChange(e.target.value)}
                maxLength={255}
                autoFocus
                required
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onCloseResourceEdit} disabled={resourceEditPending}>
            {t('common.cancel', { defaultValue: 'キャンセル' })}
          </Button>
          <Button variant="primary" onClick={onSubmitResourceEdit} disabled={resourceEditPending}>
            {resourceEditPending
              ? t('common.saving', { defaultValue: '保存中…' })
              : t('common.save', { defaultValue: '保存' })}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={deleteTarget != null} onHide={onCloseDeleteModal} centered>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            onSubmitDelete();
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title>{t('reservation.board.deletePasswordTitle', { defaultValue: '予約の削除' })}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {deleteSummary ? (
              <p className="mb-2 fw-semibold" data-rs-delete-summary>
                {deleteSummary}
              </p>
            ) : null}
            {deleteTarget?.hasDeletePassword ? (
              <>
                <p className="mb-3">
                  {t('reservation.board.deletePasswordPrompt', {
                    defaultValue:
                      'この予約を削除するには、登録時に設定した削除パスワードを入力してください。',
                  })}
                </p>
                {deleteErrorMessage ? (
                  <Alert variant="danger" className="py-2 px-3 mb-3" role="alert">
                    {deleteErrorMessage}
                  </Alert>
                ) : null}
                <Form.Group>
                  <ReservationFormLabel
                    label={t('reservation.board.deletePassword', { defaultValue: '削除パスワード' })}
                    t={t}
                  />
                  <Form.Control
                    type="password"
                    value={deletePasswordInput}
                    onChange={(e) => onDeletePasswordInputChange(e.target.value)}
                    autoFocus
                    isInvalid={Boolean(deleteErrorMessage)}
                    aria-invalid={Boolean(deleteErrorMessage)}
                  />
                </Form.Group>
              </>
            ) : (
              <p className="mb-0">
                {t('reservation.board.confirmDeleteSimple', {
                  defaultValue: 'この予約を削除します。よろしいですか？',
                })}
              </p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              type="button"
              variant="secondary"
              onClick={onCloseDeleteModal}
              disabled={deletePending}
            >
              {t('common.cancel', { defaultValue: 'キャンセル' })}
            </Button>
            <Button type="submit" variant="danger" disabled={deletePending} autoFocus={!deleteTarget?.hasDeletePassword}>
              {t('common.delete', { defaultValue: '削除' })}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showOverlapModal} onHide={onCloseOverlapModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t('reservation.board.overlapTitle', { defaultValue: '予約できません' })}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t('reservation.board.overlapMessage', {
            defaultValue:
              '指定した日時は、同じ項目の既存予約と重なっています。別の時間帯を選んでから再度登録してください。',
          })}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={onCloseOverlapModal}>
            {t('common.ok', { defaultValue: 'OK' })}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
